import { Dot, FountainPen, Headset, Lightbulb, Phone, CheckCircle } from '@/components/pages/icons';
import type { MessageProps } from './type';
import Alert from '@/components/pages/alert';
import clsx from 'clsx';

const steps = [
  {
    icon: Headset,
    title: "We're reviewing your case now",
    description: 'In progress',
  },
  {
    icon: Phone,
    title: "We'll contact you shortly to go over your case",
    description: 'Expect a call in a few minutes',
  },
  {
    icon: FountainPen,
    title: 'If we can help, you can easily sign up online or over the phone',
    description: 'Takes ~3 mins',
  },
];

export default function SuccessMessage({
  variant = 'dark',
  className,
  appendMessage,
}: MessageProps) {
  const totalSteps = steps.length;

  const alertClasses: Record<typeof variant, string> = {
    dark: '**:data-alert-success:mt-6 **:data-alert-success:@lg:mt-0',
    light: '**:data-alert-success:mt-6 **:data-alert-success:@lg:mt-0',
  };

  const titleClasses: Record<typeof variant, string> = {
    dark: '**:data-title:pt-8 **:data-title:text-[32px] **:data-title:font-normal **:data-title:leading-[120%] **:data-title:tracking-[-0.96px] **:data-title:@lg:text-5xl **:data-title:@lg:leading-[110%] **:data-title:@lg:tracking-[-1.44px]',
    light:
      '**:data-title:text-white **:data-title:pt-8 **:data-title:text-[24px] **:data-title:font-bold **:data-title:leading-[130%] **:data-title:tracking-[-0.48px]',
  };

  const itemClasses: Record<typeof variant, string> = {
    dark: `
    **:data-item:**:data-item-circle:bg-[#E5E7EB]
    **:first:data-item:**:data-item-circle:bg-brand-accent
    **:first:data-item:**:data-item-circle:text-white

    **:data-item:**:data-item-description:font-medium
    **:data-item:**:data-item-description:text-[#6B7280]
    **:first:data-item:**:data-item-description:text-brand-accent
    **:first:data-item:**:data-item-description:font-bold
    `,
    light: `
      **:data-item:**:data-item-circle:bg-[#E5E7EB]
      **:data-item:**:data-item-circle:text-brand-default
      **:first:data-item:**:data-item-circle:bg-brand-accent
      **:first:data-item:**:data-item-circle:text-white

      **:data-item:**:data-item-title:text-white

      **:data-item:**:data-item-description:font-medium
      **:data-item:**:data-item-description:text-[#E0F2FE]
      **:first:data-item:**:data-item-description:font-bold
      `,
  };

  return (
    <div
      className={clsx(
        alertClasses[variant],
        titleClasses[variant],
        itemClasses[variant],
        className
      )}
    >
      <Alert variant="success" icon={<CheckCircle />} data-alert-success>
        <p>Thanks, we received your case details!</p>
      </Alert>

      <h2 data-title>Here’s what’s next</h2>
      <ul className="flex flex-col gap-6 mb-8 pt-2">
        {steps.map((step, index) => (
          <li className={clsx('flex items-start gap-2 relative')} key={`item-${index}`} data-item>
            <div
              className="shrink-0 flex items-center justify-center w-10 h-10 rounded-full z-10 box-content"
              data-item-circle
            >
              <step.icon className="w-5" />
            </div>
            <div className="flex flex-col items-start gap-1">
              <p className="text-base leading-normal" data-item-title>
                {step.title}
              </p>
              <div className="flex items-center gap-2">
                {index === 0 && (
                  <Dot
                    className={clsx(
                      'w-3  animate-pulse ',
                      variant === 'dark'
                        ? 'text-brand-accent'
                        : 'text-brand-accent *:first:text-[#E0F2FE] *:first:opacity-100'
                    )}
                  />
                )}
                <p className="text-sm leading-5 tracking-[0.21px]" data-item-description>
                  {step.description}
                </p>
              </div>
              {index < totalSteps - 1 && (
                <div
                  className={clsx(
                    'absolute left-4.5 top-11 w-0.5 h-[calc(100%-24px)]',
                    variant === 'dark' ? 'bg-black/20' : 'bg-white'
                  )}
                >
                  {index === 0 && <div className="bg-brand-accent h-1/2 w-full"></div>}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>

      <Alert icon={<Lightbulb />} data-alert-info>
        <div>
          <p className="font-bold">Pro tips:</p>
          <ul className="list-disc pl-4">
            <li>If you&apos;ve been hurt, see a doctor ASAP</li>
            <li>Save any medical bills: We&apos;ll use those to build your case</li>
            <li>Document everything: Take pictures of injuries, damage to property or vehicles</li>
          </ul>
        </div>
      </Alert>

      {appendMessage}
    </div>
  );
}
